<template>
    <div class="d-flex flex-wrap flex-stack my-5">
        <router-link to="/system/admin" class="btn btn-primary align-self-center">
            <span class="svg-icon svg-icon-2">
                <inline-svg src="/media/icons/duotune/arrows/arr021.svg" />
            </span>
            {{ $t("pages.system.admin.title") }}
        </router-link>
    </div>
    <custom-table
        :title="$t('pages.system.admin.group.title')"
        :subTitle="$t('pages.system.admin.group.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange">
        <template v-slot:code="{ row: record }">
            {{ record.code ?? "-" }}
        </template>
        <template v-slot:status="{ row: record }">
            <span :class="record.active ? 'badge-light-success': 'badge-light-danger'" class="badge">{{ record.active ? $t('common.active') : $t('common.passive') }}</span>
        </template>
        <template v-slot:created_at="{ row: record }">
            {{ $moment(record.created_at).format("DD.MM.YYYY - HH:mm:ss") }}
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('pages.system.admin.group.endpoint.title')" placement="top" v-if="record.is_editable">
                    <router-link :to="'/system/admin/group/endpoint/'+ record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/general/gen048.svg" />
                        </span>
                    </router-link>
                </el-tooltip>
                <el-tooltip :content="$t('pages.system.admin.group.clone')" placement="top" v-if="record.code != 'DEVELOPER'">
                    <a v-on:click="cloneAdminGroup(record)" :data-kt-indicator="cloneGroup.loading ? 'on' : null" :class="cloneGroup.loading && 'pe-none'" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-1" v-if="!cloneGroup.loading">
                            <inline-svg src="/media/icons/duotune/abstract/abs027.svg" />
                        </span>
                        <span v-if="cloneGroup.loading" class="indicator-progress">
                            <span class="spinner-border spinner-border-sm align-middle"></span>
                        </span>
                    </a>
                </el-tooltip>
                <a v-on:click="fetchAdminGroup(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" v-if="record.is_editable">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/art/art005.svg" />
                    </span>
                </a>
                <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])" v-if="record.is_deletable">
                    <template #reference>
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </template>
                </el-popconfirm>
            </div>
        </template>
    </custom-table>
    <div class="modal fade" id="kt_modal_add_admin_group" ref="addAdminGroupModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header" id="kt_modal_add_customer_header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg" />
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmit()" :model="form.data" ref="adminGroupForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.system.admin.group.cols.code') }}</label>
                                <el-form-item prop="code">
                                    <el-input v-model="form.data.code" type="text"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.system.admin.group.cols.name') }}</label>
                                <el-form-item prop="name" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.data.name" type="text"/>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="fs-6 fw-bold mb-2">{{ $t('pages.system.admin.group.cols.status') }}</label>
                                <el-form-item prop="active">
                                    <el-radio-group v-model="form.data.active">
                                        <el-radio-button :label="true">{{ $t("common.active") }}</el-radio-button>
                                        <el-radio-button :label="false">{{ $t("common.passive") }}</el-radio-button>
                                    </el-radio-group>
                                </el-form-item>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{  $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "admin",
    components: {
        CustomTable
    },
    data(){
        return {
            fields: [
                {
                    name: this.$t("pages.system.admin.group.cols.id"),
                    key: "id"
                },
                {
                    name: this.$t("pages.system.admin.group.cols.code"),
                    scopedSlots: {customRender: "code"}
                },
                {
                    name: this.$t("pages.system.admin.group.cols.name"),
                    key: "name"
                },
                {
                    name: this.$t("pages.system.admin.cols.status"),
                    scopedSlots: {customRender: "status"}
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {}
            },
            selectedRowKeys: [],
            cloneGroup: {
                loading: false
            }
        }
    },
    computed: {
        table() {
            return this.$store.state.system.admin.group.table;
        }
    },
    mounted(){
        this.setCurrentPageBreadcrumbs(this.$t("pages.system.admin.group.title"), [this.$t("menu.systemManagement"), this.$t("menu.admins")]);
        this.$store.dispatch('system/admin/group/get', {page: {}});
    },
    methods:{
        newAdminGroup(){
            this.form.updateStatus = false;
            this.form.data = {
                active: true
            };
            this.form.title = this.$t("pages.system.admin.group.newAdminGroup");
            this.showModal(this.$refs.addAdminGroupModal);
        },
        fetchAdminGroup(record) {
            this.form.updateStatus = true;
            this.form.title =  this.$t("pages.system.admin.group.editAdminGroup")
            this.axios.get(this.endpoints['admin_group']+ '/' + record.id).then(response => {
                this.form.data = response.data.data;
                this.showModal(this.$refs.addAdminGroupModal);
            });
        },
        onSubmit(){
            this.$refs.adminGroupForm.validate((valid) => {
                if(valid) {
                    this.form.loading = true;

                    if(this.form.data.id) {
                        this.axios.put(this.endpoints['admin_group'] + '/' + this.form.data.id, this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                this.refreshTable();
                                this.hideModal(this.$refs.addAdminGroupModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }else {
                        this.axios.post(this.endpoints['admin_group'], this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                this.refreshTable();
                                this.hideModal(this.$refs.addAdminGroupModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });
        },
        deleteRecord(id){
            this.$store.dispatch("system/admin/group/delete", {
                id: id
            }).then((successDeleted) => {
                this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;

                case "new":
                    this.newAdminGroup();
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        refreshTable(){
            this.$store.dispatch("system/admin/group/refresh");
        },
        handleTableChange(pagination, filterData) {
            this.$store.dispatch("system/admin/group/get", {
                page: pagination,
                filterData: filterData
            });
        },
        handleSelectedRow(record){
            this.selectedRowKeys = record;
        },
        cloneAdminGroup(record) {
            this.cloneGroup.loading = true;

            this.axios.post(this.endpoints['admin_group_duplicate'], {
                source_id: record.id
            }).then(response => {
                this.onResponse(response.data, () => {
                    this.refreshTable();
                });
            }).catch(error => {
                this.onResponseFailure(error.response.data);
            }).finally(() => {
                this.cloneGroup.loading = false;
            });
        }
    }
}
</script>

<style scoped>

</style>